import { useState, useEffect } from "react";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  Switch,
  Spin,
  Checkbox,
  Divider,
  Card,
} from "antd";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
  Circle,
} from "react-leaflet";
import { AimOutlined } from "@ant-design/icons";
import "leaflet/dist/leaflet.css";
import markerIconPng from "leaflet/dist/images/marker-icon.png";
import { Icon } from "leaflet";
import { OpenStreetMapAutocomplete } from "@amraneze/osm-autocomplete";
import "./LocationDetails.scss";
import DownArrow from "../../../../../Assets/svg/downArrow.svg";
import { debounce } from "lodash";
import getAddress from "./getAddress";
import { fallbackState, manualStateRequired } from "./locationFallback";

const LocationMarker = ({ position, setPosition, form, onPick }) => {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      setPosition([lat, lng]);
      form.setFieldsValue({
        latitude: lat.toFixed(6),
        longitude: lng.toFixed(6),
      });
      onPick(lat, lng);
    },
  });


  return position ? (
    <Marker
      position={position}
      icon={
        new Icon({
          iconUrl: markerIconPng,
          iconSize: [25, 41],
          iconAnchor: [12, 41],
        })
      }
    />
  ) : null;
};

const MapWithInvalidateSize = ({ children }) => {
  const map = useMap();

  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 100);
  }, [map]);

  return <>{children}</>;
};

const MapCenterUpdater = ({ position }) => {
  const map = useMap();

  useEffect(() => {
    if (position && position[0] !== null && position[1] !== null) {
      map.setView(position, map.getZoom(), {
        animate: true,
      });
    }
  }, [position, map]);

  return null;
};

const LocationDetails = ({
  form,
  address,
  setAddress,
  isEditing,
  parishCheck,
  setParishCheck,
  selectedIpPlan,
  showHideIpPlanDetails,
  setShowHideIpPlanDetails,
  accuracy,
  setAccuracy,
  state,
}) => {
  const [latitude, setLatitude] = useState(0.0);
  const [longitude, setLongitude] = useState(0.0);
  const [position, setPosition] = useState([0.0, 0.0]);
  const [loading, setLoading] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(isEditing ? 13 : 2);
  const [mapLoading, setMapLoading] = useState(false);
  const lat = form.getFieldValue("latitude");
  const lon = form.getFieldValue("longitude");

  const seeIpPlanDetails = () => {
    setShowHideIpPlanDetails(!showHideIpPlanDetails);
  };

  useEffect(() => {
    const lat = form.getFieldValue("latitude");
    const lng = form.getFieldValue("longitude");
    // getAddress skips invalid coordinates on its own and clears the spinner
    // when its own request finishes — clearing it here raced the lookup and
    // left the card showing "Address not found" while the answer was still in
    // flight.
    getAddress(mapLoading, setMapLoading, setAddress, lat, lng);
    if (isEditing && lat && lon) {
      const parsedLat = parseFloat(lat);
      const parsedLon = parseFloat(lon);
      if (!isNaN(parsedLat) && !isNaN(parsedLon)) {
        setLatitude(parsedLat);
        setLongitude(parsedLon);
        setPosition([parsedLat, parsedLon]);
      }
    }
  }, [form, isEditing, lat, lon]);

  

  useEffect(() => {
    if (!isNaN(latitude) && !isNaN(longitude)) {
      setPosition([latitude, longitude]);
    } else {
      setPosition([0, 0]); // Default to a neutral position if invalid
    }
  }, [latitude, longitude]);

  useEffect(() => {
    const handleValuesChange = () => {
      const lat = form.getFieldValue("latitude");
      const lon = form.getFieldValue("longitude");

      const parsedLat = parseFloat(lat);
      const parsedLon = parseFloat(lon);
      if (!isNaN(parsedLat) && !isNaN(parsedLon)) {
        setLatitude(parsedLat);
        setLongitude(parsedLon);
      } else {
        setPosition([0, 0]); // Reset position if values are invalid
      }
      setLoading(false); // Data has been loaded
    };

    form.onValuesChange = handleValuesChange;

    return () => {
      form.onValuesChange = () => {}; // Cleanup
    };
  }, [form]);

  // Every way of moving the pin (search result, map click, coordinate input)
  // has to re-resolve the address — clicking the map used to move the marker
  // without ever asking the geocoder again.
  const refreshAddress = (nextLat, nextLng) => {
    getAddress(mapLoading, setMapLoading, setAddress, nextLat, nextLng);
  };

  const handleGetCurrentLocation = () => {
    setMapLoading(true);
    setZoomLevel(13);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          setLatitude(latitude);
          setLongitude(longitude);
          setPosition([latitude, longitude]);
          setAccuracy(accuracy.toFixed(2));
          form.setFieldsValue({
            latitude: latitude.toFixed(6),
            longitude: longitude.toFixed(6),
            accuracy: accuracy.toFixed(2),
          });
          // Set zoom level to 13 when current location is set
          setLoading(false); // Data has been loaded
          setMapLoading(false);
          refreshAddress(latitude, longitude);
        },
        (error) => {
          console.error(error);
          setLoading(false); // Ensure loading is turned off even if there's an error
          setMapLoading(false);
        }
      );
    } else {
      setLoading(false); // Ensure loading is turned off even if geolocation is not supported
      setMapLoading(false);
    }
  };

  const selectLocation = (data) => {
    const { lat, lon } = data;
    if (data) {
      const parsedLat = parseFloat(lat);
      const parsedLon = parseFloat(lon);
      if (!isNaN(parsedLat) && !isNaN(parsedLon)) {
        setLatitude(parsedLat);
        setLongitude(parsedLon);
        setAccuracy(0);
        setPosition([parsedLat, parsedLon]);
        form.setFieldsValue({
          latitude: parsedLat.toFixed(6),
          longitude: parsedLon.toFixed(6),
          accuracy: 0,
        });
        setZoomLevel(13); // Optionally set zoom level to 13 on search
        setLoading(false); // Data has been loaded
        refreshAddress(parsedLat, parsedLon);
      }
    }
  };

  const handleFieldChange = debounce((e) => {
    const { name, value } = e.target;
    const parsedValue = parseFloat(value);

    if (isNaN(parsedValue)) return; // Ignore invalid input

    if (name === "latitude") {
      setLatitude(parsedValue);

      getAddress(mapLoading, setMapLoading, setAddress, parsedValue, longitude);
    } else if (name === "longitude") {
      setLongitude(parsedValue);
      getAddress(mapLoading, setMapLoading, setAddress, latitude, parsedValue);
    } else if (name === "accuracy") {
      setAccuracy(parsedValue);
      getAddress(mapLoading, setMapLoading, setAddress, latitude, longitude);
    }
  }, 500);

  // What the map itself resolved, what we would fall back to, and whether the
  // user is left with no choice but to type a state in by hand.
  const manualState = Form.useWatch("manualState", form);
  const resolvedState = address?.state;
  const stateFallback = fallbackState({
    address,
    currentState: state,
    selectedIpPlan,
  });
  const stateIsMandatory = manualStateRequired({
    address,
    currentState: state,
    selectedIpPlan,
  });

  const handleParishCheckChange = (e) => {
    const checked = e.target.checked;
    setParishCheck(checked);

    if (!checked) {
      form.setFieldsValue({ parish: undefined });
    }
  };

  return (
    <>
      <div>
        {selectedIpPlan == undefined ||
        selectedIpPlan == null ||
        selectedIpPlan == "" ? (
          <></>
        ) : (
          <button
            style={{
              marginTop: "0",
              marginBottom: "15px",
              border: "none",
              backgroundColor: "transparent",
              color: "var(--2, #AA1A5F)",
              fontWeight: "500",
            }}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              seeIpPlanDetails();
            }}
          >
            {showHideIpPlanDetails
              ? "Hide IP Plan details"
              : "Show IP Plan details"}

            <img src={DownArrow} alt="toggle" />
          </button>
        )}
        {showHideIpPlanDetails && (
          <div className="teamDetails mb-5">
            <Card title="Ip Plan General Details">
              <Divider />
              <div className="d-flex flex-row flex-wrap gap-5">
                <p className="d-flex flex-column">
                  <span style={{ fontWeight: "700" }}>
                    Implementing Partner:
                  </span>{" "}
                  {selectedIpPlan?.implementingPartner?.name}
                </p>
                <p className="d-flex flex-column">
                  <span style={{ fontWeight: "700" }}>Governorate/State:</span>{" "}
                  {selectedIpPlan?.ipPlanStates
                    ?.map((state) => state?.stateName)
                    .join(", ")}
                </p>
                {selectedIpPlan?.ipPlanCities?.length > 0 && (
                  <p className="d-flex flex-column">
                    <span style={{ fontWeight: "700" }}>
                      City/Disctrict/Municipality:
                    </span>{" "}
                    {selectedIpPlan?.ipPlanCities
                      ?.map((city) => city?.name)
                      .join(", ")}
                  </p>
                )}
                <p className="d-flex flex-column">
                  <span style={{ fontWeight: "700" }}>
                    Assigned to a Field Coordinator:
                  </span>{" "}
                  {selectedIpPlan?.assignee?.userDetails?.firstName
                    ? `${selectedIpPlan?.assignee?.userDetails?.firstName}
                     ${selectedIpPlan?.assignee?.userDetails?.lastName}`
                    : selectedIpPlan?.assignee?.email}
                </p>
              </div>
            </Card>
          </div>
        )}
        <div className="container">
          <div className="row">
            <div className="col-12 col-md-6 col-lg-6">
              <Form.Item
                label="Latitude (x°)"
                name="latitude"
                initialValue={isEditing ? latitude : 0} // Set default value for adding
                rules={[
                  { required: true, message: "Please enter latitude" },
                  {
                    validator: (_, value) => {
                      const numValue = parseFloat(value);
                      if (numValue > 90 || numValue < -90) {
                        return Promise.reject(
                          "Latitude must be between -90 and 90"
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  type="number"
                  min={-90}
                  max={90}
                  name="latitude"
                  placeholder="Enter latitude value"
                  onChange={handleFieldChange}
                />
              </Form.Item>
              <Form.Item
                label="Longitude (y°)"
                name="longitude"
                initialValue={isEditing ? longitude : 0}
                rules={[
                  { required: true, message: "Please enter longitude" },
                  {
                    validator: (_, value) => {
                      const numValue = parseFloat(value);
                      if (numValue > 180 || numValue < -180) {
                        return Promise.reject(
                          "Longitude must be between -180 and 180"
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  min={-180}
                  max={180}
                  type="number"
                  name="longitude"
                  placeholder="Enter longitude value"
                  onChange={handleFieldChange}
                />
              </Form.Item>
              <Form.Item
                label="Accuracy"
                name="accuracy"
                initialValue={isEditing ? accuracy : 0}
                rules={[{ required: true, message: "Please enter accuracy" }]}
              >
                <Input
                  min={0}
                  name="accuracy"
                  type="number"
                  placeholder="Enter accuracy value"
                  onChange={handleFieldChange}
                />
              </Form.Item>

              <div className="w-100 d-flex flex-column gap-1 mb-4">
                <Form.Item name="parishCheck" className="m-0 p-0">
                  <Checkbox
                    value={parishCheck}
                    checked={parishCheck}
                    onChange={handleParishCheckChange}
                    id="parish"
                  >
                    <span className="parish">Parish </span>
                    <span
                      style={{
                        fontWeight: "400",
                        fontSize: "14px",
                        color: "gray",
                      }}
                    >
                      (optional)
                    </span>
                  </Checkbox>
                </Form.Item>
                {parishCheck && (
                  <Form.Item name="parish" className="m-0 p-2">
                    <Input type="text" placeholder="Please enter parish" />
                  </Form.Item>
                )}
              </div>
              <Form.Item label="Selected Address" style={{ marginBottom: 24 }}>
                <p
                  style={{ fontSize: "14px", lineHeight: "1.8" }}
                  className="border rounded p-3 mb-1 bg-light"
                >
                  {address?.parish ||
                    address?.closestAddress ||
                    (manualState || "").trim() ||
                    "Address not found"}
                </p>
                {!address?.parish && address?.closestAddress && (
                  <span style={{ fontSize: "12px", color: "#888" }}>
                    The map has no exact address for this pin — this is the
                    closest place it could find.
                  </span>
                )}
              </Form.Item>
              {/* The backend cannot store a card without a state. When the map
                  fails to name one we take it from the user, and only insist on
                  it when there is nothing to fall back on. */}
              {!resolvedState && (
                <Form.Item
                  label={
                    <span>
                      Map is unable to locate this state, write on your own
                      {stateIsMandatory ? " (required)" : " (optional)"}
                      {stateFallback && (
                        <span
                          style={{
                            color: "#888",
                            fontWeight: 400,
                            marginLeft: 8,
                          }}
                        >
                          (Current state: <b>{stateFallback}</b> — saved if you
                          leave this empty)
                        </span>
                      )}
                    </span>
                  }
                  name="manualState"
                  rules={[
                    {
                      required: stateIsMandatory,
                      whitespace: true,
                      message:
                        "The map could not identify a state for this location — please enter it.",
                    },
                  ]}
                >
                  <Input
                    placeholder={
                      stateFallback
                        ? `Leave empty to use "${stateFallback}"`
                        : "Enter state"
                    }
                  />
                </Form.Item>
              )}
            </div>
            <div className="col-12 col-md-6 col-lg-6 mt-md-4 mt-lg-4">
              <div className="d-flex flex-row align-items-start gap-3">
                <Button
                  onClick={handleGetCurrentLocation}
                  className="d-flex justify-center items-center"
                  icon={<AimOutlined style={{ color: "#999" }} />}
                />
                <Form.Item className="w-100" name="search">
                  <div
                    className="osm-search"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                      }
                    }}
                  >
                    <OpenStreetMapAutocomplete
                      placeholder="Search address..."
                      value={null}
                      onChange={(val) => selectLocation(val)}
                    />
                  </div>
                </Form.Item>
              </div>

              <Spin spinning={mapLoading}>
                <div
                  className=" w-100"
                  style={{
                    height: "400px",
                    position: "relative",
                    zIndex: 0,
                  }}
                >
                  <MapContainer
                    center={position}
                    zoom={zoomLevel}
                    style={{ height: "100%", width: "100%" }}
                    maxBounds={[
                      [-90, -180],
                      [90, 180],
                    ]}
                    maxBoundsViscosity={1.0}
                  >
                    <MapWithInvalidateSize>
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      {!loading && position && (
                        <LocationMarker
                          position={position}
                          setPosition={setPosition}
                          form={form}
                          onPick={refreshAddress}
                        />
                      )}
                      {position && accuracy && (
                        <Circle
                          center={position}
                          radius={accuracy}
                          pathOptions={{ color: "blue", fillOpacity: 0.2 }}
                        />
                      )}

                      <MapCenterUpdater position={position} />
                    </MapWithInvalidateSize>
                  </MapContainer>
                </div>
              </Spin>
            </div>
          </div>
        </div>
        <Row gutter={16} className="mt-5 lg-mt-0">
          <Col
            span={12}
            style={{

              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Form.Item
                name="isVirtual"
                style={{ marginBottom: "0" }}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <span>This Visit Will Be Virtual</span>
            </div>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default LocationDetails;
